import { useState, useEffect, useCallback } from 'react';
import { View, Text, ScrollView, Pressable, Alert, StyleSheet, ActivityIndicator, Modal, Platform, Dimensions } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { useAuthStore } from '../../../../../../store/authStore';
import { authApi } from '../../../../../../shared/utils/api';
import { useTheme } from '../../../../../../contexts/ThemeContext';
import { createStyles, addColorOpacity } from '../../../../../../shared/utils/styles';
import { DesignTokens } from '../../../../../../shared/types/tokens';
import { MortgageCalculation, CreateMortgageCalculationRequest, DownPaymentType, MortgagePartnerBreakdown } from '../../../../../../shared/types/mortgage';
import { MortgageCalculatorInput } from '../../../../../../components/mortgage/MortgageCalculatorInput';
import { MortgageCalculatorResults } from '../../../../../../components/mortgage/MortgageCalculatorResults';
import { PartnerBreakdownCard } from '../../../../../../components/mortgage/PartnerBreakdownCard';
import { MortgageCalculationCard } from '../../../../../../components/mortgage/MortgageCalculationCard';
import { YearByYearSummary } from '../../../../../../components/mortgage/YearByYearSummary';
import { AfterXYearsView } from '../../../../../../components/mortgage/AfterXYearsView';
import { calculateMortgage } from '../../../../../../shared/utils/mortgage-calculator';
import { useResponsive, getResponsiveValue } from '../../../../../../shared/utils/responsive';
import { BackButton } from '../../../../../../components/ui';
import { PageContainer } from '../../../../../../components/layout';
import { logger } from '../../../../../../shared/utils/logger';

export default function MortgageCalculatorPage() {
  const { id, propertyId } = useLocalSearchParams<{ id: string; propertyId: string }>();
  const [property, setProperty] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [calculations, setCalculations] = useState<MortgageCalculation[]>([]);
  const [loadingCalculations, setLoadingCalculations] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedCalculation, setSelectedCalculation] = useState<MortgageCalculation | null>(null);
  const [selectedBreakdowns, setSelectedBreakdowns] = useState<MortgagePartnerBreakdown[]>([]);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [calculationToDelete, setCalculationToDelete] = useState<MortgageCalculation | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // Comparison state
  const [selectedForComparison, setSelectedForComparison] = useState<Set<number>>(new Set());
  const [compareModalVisible, setCompareModalVisible] = useState(false);
  const [comparisonCalculations, setComparisonCalculations] = useState<MortgageCalculation[]>([]);
  const [comparisonBreakdowns, setComparisonBreakdowns] = useState<Record<number, MortgagePartnerBreakdown[]>>({});
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [propertyPartners, setPropertyPartners] = useState<any[]>([]);
  
  // Amortization schedule state
  const [scheduleModalVisible, setScheduleModalVisible] = useState(false);
  const [scheduleCalculation, setScheduleCalculation] = useState<MortgageCalculation | null>(null);
  const [amortizationSchedule, setAmortizationSchedule] = useState<any[]>([]);
  const [scheduleView, setScheduleView] = useState<'all' | 'yearly' | 'afterX'>('all');
  
  // Form state
  const [propertyValue, setPropertyValue] = useState('');
  const [originalPropertyValue, setOriginalPropertyValue] = useState<number | null>(null);
  const [downPayment, setDownPayment] = useState('');
  const [downPaymentType, setDownPaymentType] = useState<DownPaymentType>('amount');
  const [interestRate, setInterestRate] = useState('');
  const [loanTermYears, setLoanTermYears] = useState(30);
  const [startDate, setStartDate] = useState('');
  
  // Preview calculation
  const [previewCalculation, setPreviewCalculation] = useState<any>(null);
  const [creating, setCreating] = useState(false);

  const router = useRouter();
  const { tokens } = useTheme();
  const utils = createStyles(tokens);
  const { isDesktop, isTablet, isMobile, width } = useResponsive();
  
  // Also check window width directly for web
  const [windowWidth, setWindowWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      return Dimensions.get('window').width;
    }
    return 0;
  });

  useEffect(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const updateWidth = () => setWindowWidth(Dimensions.get('window').width);
      const subscription = Dimensions.addEventListener('change', updateWidth);
      return () => subscription?.remove();
    }
  }, []);

  const isDesktopLayout = isDesktop || (Platform.OS === 'web' && windowWidth >= 1024);
  const isTabletLayout = isTablet || (Platform.OS === 'web' && windowWidth >= 768 && windowWidth < 1024);
  const isMobileLayout = isMobile || (Platform.OS === 'web' && windowWidth < 768);
  
  // Inline calculator visibility state
  const [showInlineCalculator, setShowInlineCalculator] = useState(!isMobileLayout);
  const [previewBreakdowns, setPreviewBreakdowns] = useState<MortgagePartnerBreakdown[]>([]);
  
  const styles = makeStyles(tokens, utils, isDesktopLayout, isTabletLayout, isMobileLayout);

  const loadProperty = useCallback(async () => {
    try {
      setLoading(true);
      const portfolioId = parseInt(id as string, 10);
      const propId = parseInt(propertyId as string, 10);
      if (isNaN(portfolioId) || isNaN(propId)) {
        Alert.alert('Error', 'Invalid portfolio or property ID');
        router.back();
        return;
      }

      const response = await authApi.getProperty(portfolioId, propId);
      setProperty(response.property);
      
      // Pre-fill property value if available
      if (response.property.property_value) {
        const propVal = response.property.property_value;
        setPropertyValue(propVal.toString());
        setOriginalPropertyValue(propVal);
      }
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to load property');
      router.back();
    } finally {
      setLoading(false);
    }
  }, [id, propertyId, router]);

  const loadCalculations = useCallback(async () => {
    setLoadingCalculations(true);
    try {
      const portfolioId = parseInt(id as string, 10);
      const propId = parseInt(propertyId as string, 10);
      const response = await authApi.getMortgageCalculations(portfolioId, propId);
      setCalculations(response.calculations);
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to load calculations');
    } finally {
      setLoadingCalculations(false);
    }
  }, [id, propertyId]);

  useEffect(() => {
    if (id && propertyId) {
      loadProperty();
      loadCalculations();
    }
  }, [id, propertyId, loadProperty, loadCalculations]);

  // Load property partners for preview breakdown
  useEffect(() => {
    const loadPartners = async () => {
      if (property && property.partners) {
        setPropertyPartners(property.partners);
      }
    };
    loadPartners();
  }, [property]);

  // Calculate preview when inputs change
  useEffect(() => {
    if (propertyValue && downPayment && interestRate && loanTermYears) {
      try {
        const propVal = parseFloat(propertyValue);
        const downPay = parseFloat(downPayment);
        const intRate = parseFloat(interestRate);
        
        if (!isNaN(propVal) && propVal > 0 && !isNaN(downPay) && downPay >= 0 && !isNaN(intRate) && intRate >= 0 && loanTermYears > 0) {
          const result = calculateMortgage({
            propertyValue: propVal,
            downPayment: downPay,
            downPaymentType: downPaymentType,
            interestRate: intRate,
            loanTermYears: loanTermYears,
            startDate: startDate || null,
          });
          setPreviewCalculation(result);
          
          // Calculate partner breakdowns for preview
          if (result && propertyPartners.length > 0) {
            const breakdowns: MortgagePartnerBreakdown[] = propertyPartners.map((partner: any) => {
              const ownership = partner.ownership_percentage || 0;
              return {
                id: 0, // Placeholder for preview
                mortgage_calculation_id: 0, // Placeholder for preview
                user_id: partner.user_id,
                ownership_percentage: ownership,
                down_payment_share: result.down_payment * (ownership / 100),
                monthly_payment_share: result.monthly_payment * (ownership / 100),
                total_interest_share: result.total_interest * (ownership / 100),
                total_paid_share: result.total_paid * (ownership / 100),
              };
            });
            setPreviewBreakdowns(breakdowns);
          } else {
            setPreviewBreakdowns([]);
          }
        } else {
          setPreviewCalculation(null);
          setPreviewBreakdowns([]);
        }
      } catch (error) {
        logger.error('Error calculating mortgage preview', error, 'mortgage');
        setPreviewCalculation(null);
        setPreviewBreakdowns([]);
      }
    } else {
      setPreviewCalculation(null);
      setPreviewBreakdowns([]);
    }
  }, [propertyValue, downPayment, downPaymentType, interestRate, loanTermYears, startDate, propertyPartners]);

  const handleCreateCalculation = async () => {
    logger.debug('Creating mortgage calculation', 'mortgage', { propertyValue, downPayment, interestRate, loanTermYears });
    
    if (!propertyValue || !downPayment || !interestRate) {
      Alert.alert('Validation Error', 'Please fill in all required fields.');
      return;
    }

    const propVal = parseFloat(propertyValue);
    const downPay = parseFloat(downPayment);
    const intRate = parseFloat(interestRate);

    if (isNaN(propVal) || propVal <= 0) {
      Alert.alert('Validation Error', 'Property value must be a positive number.');
      return;
    }

    if (isNaN(downPay) || downPay < 0) {
      Alert.alert('Validation Error', 'Down payment must be a non-negative number.');
      return;
    }

    if (isNaN(intRate) || intRate < 0) {
      Alert.alert('Validation Error', 'Interest rate must be a non-negative number.');
      return;
    }

    if (!previewCalculation) {
      Alert.alert('Validation Error', 'Please wait for calculation to complete.');
      return;
    }

    setCreating(true);
    try {
      const portfolioId = parseInt(id as string, 10);
      const propId = parseInt(propertyId as string, 10);
      
      if (isNaN(portfolioId) || isNaN(propId)) {
        throw new Error('Invalid portfolio or property ID');
      }
      
      const request: CreateMortgageCalculationRequest = {
        property_id: propId,
        property_value: propVal,
        down_payment: downPay,
        down_payment_type: downPaymentType,
        interest_rate: intRate,
        loan_term_years: loanTermYears,
        start_date: startDate || null,
      };

      logger.debug('Sending mortgage calculation request', 'mortgage');
      const response = await authApi.createMortgageCalculation(portfolioId, propId, request);
      logger.info('Mortgage calculation created successfully', 'mortgage', { calculationId: response.calculation?.id });
      
      Alert.alert('Success', 'Mortgage calculation created successfully!');
      setCreateModalVisible(false);
      // Reset form but keep property value
      setDownPayment('');
      setInterestRate('');
      setStartDate('');
      setPreviewCalculation(null);
      setPreviewBreakdowns([]);
      loadCalculations();
    } catch (error: any) {
      logger.error('Failed to create mortgage calculation', error, 'mortgage');
      Alert.alert('Error', error.message || 'Failed to create calculation');
    } finally {
      setCreating(false);
    }
  };

  const handleViewCalculation = async (calculation: MortgageCalculation) => {
    try {
      const portfolioId = parseInt(id as string, 10);
      const propId = parseInt(propertyId as string, 10);
      const response = await authApi.getMortgageCalculation(portfolioId, propId, calculation.id);
      setSelectedCalculation(response.calculation);
      setSelectedBreakdowns(response.partner_breakdowns || []);
      setViewModalVisible(true);
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to load calculation details');
    }
  };

  const handleToggleComparison = (calculationId: number) => {
    setSelectedForComparison(prev => {
      const newSet = new Set(prev);
      if (newSet.has(calculationId)) {
        newSet.delete(calculationId);
      } else {
        newSet.add(calculationId);
      }
      return newSet;
    });
  };

  const handleOpenComparison = async (showPartners: boolean = false) => {
    const selected = calculations.filter(calc => selectedForComparison.has(calc.id));
    if (selected.length < 2) {
      Alert.alert('Comparison', 'Please select at least 2 calculations to compare.');
      return;
    }
    
    // Load full calculation details (not just list data) to ensure accuracy
    const portfolioId = parseInt(id as string, 10);
    const propId = parseInt(propertyId as string, 10);
    const breakdownsMap: Record<number, MortgagePartnerBreakdown[]> = {};
    const fullCalculations: MortgageCalculation[] = [];
    
    try {
      for (const calc of selected) {
        const response = await authApi.getMortgageCalculation(portfolioId, propId, calc.id);
        // Use the full calculation data from the API, not the list data
        fullCalculations.push(response.calculation);
        breakdownsMap[response.calculation.id] = response.partner_breakdowns || [];
      }
      setComparisonCalculations(fullCalculations);
      setComparisonBreakdowns(breakdownsMap);
      
      // Get property partners to populate selector
      // Use property from state if available, otherwise fetch it
      let propertyData = property;
      if (!propertyData) {
        try {
          const propResponse = await authApi.getProperty(portfolioId, propId);
          propertyData = propResponse.property;
        } catch (error: any) {
          logger.error('Error loading property for comparison', error, 'mortgage');
        }
      }
      
      if (propertyData && propertyData.partners && propertyData.partners.length > 1) {
        setPropertyPartners(propertyData.partners);
        // If showPartners is true and there are partners, default to first partner, otherwise overall view
        if (showPartners) {
          setSelectedPartnerId(propertyData.partners[0].user_id);
        } else {
          setSelectedPartnerId(null);
        }
      } else {
        setPropertyPartners([]);
        setSelectedPartnerId(null);
      }
    } catch (error: any) {
      logger.error('Error loading comparison breakdowns', error, 'mortgage');
    }
    
    setCompareModalVisible(true);
  };

  const handleViewSchedule = async (calculation: MortgageCalculation) => {
    try {
      const portfolioId = parseInt(id as string, 10);
      const propId = parseInt(propertyId as string, 10);
      const response = await authApi.getMortgageCalculation(portfolioId, propId, calculation.id);
      setScheduleCalculation(response.calculation);
      setAmortizationSchedule(response.amortization_schedule || []);
      setScheduleModalVisible(true);
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to load amortization schedule');
    }
  };

  const handleDeleteCalculation = (calculation: MortgageCalculation) => {
    setCalculationToDelete(calculation);
    setDeleteModalVisible(true);
  };

  const confirmDeleteCalculation = async () => {
    if (!calculationToDelete) return;

    setDeleting(true);
    try {
      const portfolioId = parseInt(id as string, 10);
      const propId = parseInt(propertyId as string, 10);
      await authApi.deleteMortgageCalculation(portfolioId, propId, calculationToDelete.id);
      setDeleteModalVisible(false);
      setCalculationToDelete(null);
      loadCalculations();
    } catch (error: any) {
      Alert.alert('Error', error.message || 'Failed to delete calculation.');
    } finally {
      setDeleting(false);
    }
  };

  const cancelDeleteCalculation = () => {
    setDeleteModalVisible(false);
    setCalculationToDelete(null);
  };

  if (loading) {
    return (
      <PageContainer scrollable={false}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={tokens.color.primary} />
          <Text style={styles.loadingText}>Loading...</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth={Platform.OS === 'web' ? 1400 : 'auto'}>
      <View style={styles.header}>
        <BackButton to={`/(tabs)/portfolios/${id}/properties/${propertyId}`} />
        <Text style={styles.title}>Mortgage Calculator</Text>
        {property && (
          <Text style={styles.subtitle}>{property.address}</Text>
        )}
      </View>

      {/* Inline Calculator Section */}
      <View style={styles.calculatorSection}>
        <View style={styles.calculatorSectionHeader}>
          <Text style={styles.calculatorSectionTitle}>Calculate Mortgage</Text>
          {isMobileLayout && (
            <Pressable
              onPress={() => setShowInlineCalculator(!showInlineCalculator)}
              style={styles.toggleCalculatorButton}
            >
              <Text style={styles.toggleCalculatorButtonText}>
                {showInlineCalculator ? '▼ Hide' : '▶ Show Calculator'}
              </Text>
            </Pressable>
          )}
        </View>

        {showInlineCalculator && (
          <View style={styles.calculatorContent}>
            {isDesktopLayout || isTabletLayout ? (
              // Two/Three Column Layout for Tablet/Desktop
              <View style={styles.calculatorGrid}>
                <View style={styles.calculatorInputColumn}>
                  <MortgageCalculatorInput
                    propertyValue={propertyValue}
                    onPropertyValueChange={setPropertyValue}
                    originalPropertyValue={originalPropertyValue}
                    onResetPropertyValue={() => {
                      if (originalPropertyValue !== null) {
                        setPropertyValue(originalPropertyValue.toString());
                      }
                    }}
                    downPayment={downPayment}
                    onDownPaymentChange={setDownPayment}
                    downPaymentType={downPaymentType}
                    onDownPaymentTypeChange={setDownPaymentType}
                    interestRate={interestRate}
                    onInterestRateChange={setInterestRate}
                    loanTermYears={loanTermYears}
                    onLoanTermYearsChange={setLoanTermYears}
                    startDate={startDate}
                    onStartDateChange={setStartDate}
                  />
                </View>
                <View style={styles.calculatorResultsColumn}>
                  {previewCalculation ? (
                    <>
                      <MortgageCalculatorResults
                        loanAmount={previewCalculation.loan_amount}
                        monthlyPayment={previewCalculation.monthly_payment}
                        totalInterest={previewCalculation.total_interest}
                        totalPaid={previewCalculation.total_paid}
                      />
                      {previewBreakdowns.length > 0 && (
                        <View style={styles.previewPartnerBreakdown}>
                          <Text style={styles.sectionTitle}>Partner Breakdown</Text>
                          {previewBreakdowns.map((breakdown) => {
                            const partner = propertyPartners.find((p: any) => p.user_id === breakdown.user_id);
                            return (
                              <PartnerBreakdownCard
                                key={breakdown.user_id}
                                email={partner?.email || 'Unknown'}
                                display_name={partner?.display_name}
                                ownershipPercentage={breakdown.ownership_percentage}
                                downPaymentShare={breakdown.down_payment_share}
                                monthlyPaymentShare={breakdown.monthly_payment_share}
                                totalInterestShare={breakdown.total_interest_share}
                                totalPaidShare={breakdown.total_paid_share}
                              />
                            );
                          })}
                        </View>
                      )}
                      <Pressable
                        onPress={handleCreateCalculation}
                        disabled={creating || !previewCalculation}
                        style={[
                          styles.saveCalculationButton,
                          (creating || !previewCalculation) && styles.saveCalculationButtonDisabled,
                        ]}
                      >
                        <Text style={styles.saveCalculationButtonText}>
                          {creating ? 'Saving...' : 'Save Calculation'}
                        </Text>
                      </Pressable>
                    </>
                  ) : (
                    <View style={styles.emptyPreview}>
                      <Text style={styles.emptyPreviewText}>
                        Enter values to see calculations
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            ) : (
              // Single Column Layout for Mobile
              <View>
                <MortgageCalculatorInput
                  propertyValue={propertyValue}
                  onPropertyValueChange={setPropertyValue}
                  originalPropertyValue={originalPropertyValue}
                  onResetPropertyValue={() => {
                    if (originalPropertyValue !== null) {
                      setPropertyValue(originalPropertyValue.toString());
                    }
                  }}
                  downPayment={downPayment}
                  onDownPaymentChange={setDownPayment}
                  downPaymentType={downPaymentType}
                  onDownPaymentTypeChange={setDownPaymentType}
                  interestRate={interestRate}
                  onInterestRateChange={setInterestRate}
                  loanTermYears={loanTermYears}
                  onLoanTermYearsChange={setLoanTermYears}
                  startDate={startDate}
                  onStartDateChange={setStartDate}
                />
                {previewCalculation && (
                  <>
                    <MortgageCalculatorResults
                      loanAmount={previewCalculation.loan_amount}
                      monthlyPayment={previewCalculation.monthly_payment}
                      totalInterest={previewCalculation.total_interest}
                      totalPaid={previewCalculation.total_paid}
                    />
                    {previewBreakdowns.length > 0 && (
                      <View style={styles.previewPartnerBreakdown}>
                        <Text style={styles.sectionTitle}>Partner Breakdown</Text>
                        {previewBreakdowns.map((breakdown) => {
                          const partner = propertyPartners.find((p: any) => p.user_id === breakdown.user_id);
                          return (
                            <PartnerBreakdownCard
                              key={breakdown.user_id}
                              email={partner?.email || 'Unknown'}
                              ownershipPercentage={breakdown.ownership_percentage}
                              downPaymentShare={breakdown.down_payment_share}
                              monthlyPaymentShare={breakdown.monthly_payment_share}
                              totalInterestShare={breakdown.total_interest_share}
                              totalPaidShare={breakdown.total_paid_share}
                            />
                          );
                        })}
                      </View>
                    )}
                    <Pressable
                      onPress={handleCreateCalculation}
                      disabled={creating || !previewCalculation}
                      style={[
                        styles.saveCalculationButton,
                        (creating || !previewCalculation) && styles.saveCalculationButtonDisabled,
                      ]}
                    >
                      <Text style={styles.saveCalculationButtonText}>
                        {creating ? 'Saving...' : 'Save Calculation'}
                      </Text>
                    </Pressable>
                  </>
                )}
              </View>
            )}
          </View>
        )}
      </View>

      {/* Saved Calculations Section */}
      <View style={styles.savedCalculationsSection}>
        <View style={styles.savedCalculationsHeader}>
          <Text style={styles.savedCalculationsTitle}>Saved Calculations</Text>
          <View style={styles.headerActions}>
            {selectedForComparison.size >= 2 && (
              <>
                <Pressable
                  onPress={() => handleOpenComparison(false)}
                  style={styles.compareButton}
                >
                  <Text style={styles.compareButtonText}>
                    Compare ({selectedForComparison.size})
                  </Text>
                </Pressable>
                {propertyPartners && propertyPartners.length > 1 && (
                  <Pressable
                    onPress={() => handleOpenComparison(true)}
                    style={[styles.compareButton, styles.compareByPartnersButton]}
                  >
                    <Text style={styles.compareButtonText}>
                      Compare by Partners
                    </Text>
                  </Pressable>
                )}
              </>
            )}
          </View>
        </View>

        {loadingCalculations ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={tokens.color.primary} />
            <Text style={styles.loadingText}>Loading calculations...</Text>
          </View>
        ) : calculations.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyText}>No saved calculations yet.</Text>
            <Text style={styles.emptySubtext}>
              Use the calculator above to create and save your first calculation!
            </Text>
          </View>
        ) : (
          <View style={styles.calculationsList}>
            {calculations.map((calc) => (
              <MortgageCalculationCard
                key={calc.id}
                calculation={calc}
                onView={() => handleViewCalculation(calc)}
                onDelete={() => handleDeleteCalculation(calc)}
                onCompare={() => handleToggleComparison(calc.id)}
                onViewSchedule={() => handleViewSchedule(calc)}
                isSelected={selectedForComparison.has(calc.id)}
              />
            ))}
          </View>
        )}
      </View>

      {/* Create Calculation Modal */}
      <Modal
        visible={createModalVisible}
        transparent={true}
        animationType="none"
        onRequestClose={() => setCreateModalVisible(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setCreateModalVisible(false)}
        >
          <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
            {isDesktopLayout ? (
              <View style={styles.modalDesktopLayout}>
                <View style={styles.modalDesktopLeft}>
                  <Text style={styles.modalTitle}>New Mortgage Calculation</Text>
                  <ScrollView 
                    style={styles.modalScrollView} 
                    contentContainerStyle={styles.modalScrollContent}
                    showsVerticalScrollIndicator={true}
                  >
                    <MortgageCalculatorInput
                      propertyValue={propertyValue}
                      onPropertyValueChange={setPropertyValue}
                      originalPropertyValue={originalPropertyValue}
                      onResetPropertyValue={() => {
                        if (originalPropertyValue !== null) {
                          setPropertyValue(originalPropertyValue.toString());
                        }
                      }}
                      downPayment={downPayment}
                      onDownPaymentChange={setDownPayment}
                      downPaymentType={downPaymentType}
                      onDownPaymentTypeChange={setDownPaymentType}
                      interestRate={interestRate}
                      onInterestRateChange={setInterestRate}
                      loanTermYears={loanTermYears}
                      onLoanTermYearsChange={setLoanTermYears}
                      startDate={startDate}
                      onStartDateChange={setStartDate}
                    />
                  </ScrollView>
                </View>
                <View style={styles.modalDesktopRight}>
                  <Text style={styles.modalTitle}>Live Results</Text>
                  <ScrollView 
                    style={styles.modalScrollView} 
                    contentContainerStyle={styles.modalScrollContent}
                    showsVerticalScrollIndicator={true}
                  >
                    {previewCalculation ? (
                      <MortgageCalculatorResults
                        loanAmount={previewCalculation.loan_amount}
                        monthlyPayment={previewCalculation.monthly_payment}
                        totalInterest={previewCalculation.total_interest}
                        totalPaid={previewCalculation.total_paid}
                      />
                    ) : (
                      <View style={styles.emptyPreview}>
                        <Text style={styles.emptyPreviewText}>Enter values to see calculations</Text>
                      </View>
                    )}
                  </ScrollView>
                </View>
              </View>
            ) : (
              <ScrollView style={styles.modalScrollView} contentContainerStyle={styles.modalScrollContent}>
                <Text style={styles.modalTitle}>New Mortgage Calculation</Text>

                <MortgageCalculatorInput
                  propertyValue={propertyValue}
                  onPropertyValueChange={setPropertyValue}
                  originalPropertyValue={originalPropertyValue}
                  onResetPropertyValue={() => {
                    if (originalPropertyValue !== null) {
                      setPropertyValue(originalPropertyValue.toString());
                    }
                  }}
                  downPayment={downPayment}
                  onDownPaymentChange={setDownPayment}
                  downPaymentType={downPaymentType}
                  onDownPaymentTypeChange={setDownPaymentType}
                  interestRate={interestRate}
                  onInterestRateChange={setInterestRate}
                  loanTermYears={loanTermYears}
                  onLoanTermYearsChange={setLoanTermYears}
                  startDate={startDate}
                  onStartDateChange={setStartDate}
                />

                {previewCalculation && (
                  <MortgageCalculatorResults
                    loanAmount={previewCalculation.loan_amount}
                    monthlyPayment={previewCalculation.monthly_payment}
                    totalInterest={previewCalculation.total_interest}
                    totalPaid={previewCalculation.total_paid}
                  />
                )}
              </ScrollView>
            )}

            <View style={styles.modalButtons}>
              <Pressable
                onPress={() => {
                  setCreateModalVisible(false);
                  setPropertyValue('');
                  setDownPayment('');
                  setInterestRate('');
                  setStartDate('');
                  setPreviewCalculation(null);
                }}
                style={[styles.modalButton, styles.modalButtonCancel]}
              >
                <Text style={styles.modalButtonCancelText}>Cancel</Text>
              </Pressable>
              <Pressable
                onPress={handleCreateCalculation}
                disabled={creating || !previewCalculation}
                style={[
                  styles.modalButton,
                  styles.modalButtonPrimary,
                  (creating || !previewCalculation) && styles.modalButtonDisabled,
                ]}
              >
                <Text style={styles.modalButtonPrimaryText}>
                  {creating ? 'Creating...' : 'Create Calculation'}
                </Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* View Calculation Modal */}
      <Modal
        visible={viewModalVisible}
        transparent={true}
        animationType="none"
        onRequestClose={() => setViewModalVisible(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setViewModalVisible(false)}
        >
          <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
            <ScrollView style={styles.modalScrollView} contentContainerStyle={styles.modalScrollContent}>
              <Text style={styles.modalTitle}>Mortgage Calculation Details</Text>

              {selectedCalculation && (
                <>
                  {/* Calculation Parameters */}
                  <View style={styles.calculationParametersSection}>
                    <Text style={styles.sectionTitle}>Calculation Parameters</Text>
                    <View style={styles.parameterGrid}>
                      <View style={styles.parameterItem}>
                        <Text style={styles.parameterLabel}>Property Value</Text>
                        <Text style={styles.parameterValue}>
                          ${selectedCalculation.property_value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </Text>
                      </View>
                      <View style={styles.parameterItem}>
                        <Text style={styles.parameterLabel}>Down Payment</Text>
                        <Text style={styles.parameterValue}>
                          {selectedCalculation.down_payment_type === 'percentage'
                            ? `${selectedCalculation.down_payment}%`
                            : `$${selectedCalculation.down_payment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                        </Text>
                      </View>
                      <View style={styles.parameterItem}>
                        <Text style={styles.parameterLabel}>Interest Rate</Text>
                        <Text style={styles.parameterValue}>
                          {(selectedCalculation.interest_rate * 100).toFixed(2)}%
                        </Text>
                      </View>
                      <View style={styles.parameterItem}>
                        <Text style={styles.parameterLabel}>Loan Term</Text>
                        <Text style={styles.parameterValue}>
                          {selectedCalculation.loan_term_years} years
                        </Text>
                      </View>
                      {selectedCalculation.start_date && (
                        <View style={styles.parameterItem}>
                          <Text style={styles.parameterLabel}>Start Date</Text>
                          <Text style={styles.parameterValue}>
                            {new Date(selectedCalculation.start_date).toLocaleDateString()}
                          </Text>
                        </View>
                      )}
                    </View>
                  </View>

                  <MortgageCalculatorResults
                    loanAmount={selectedCalculation.loan_amount}
                    monthlyPayment={selectedCalculation.monthly_payment}
                    totalInterest={selectedCalculation.total_interest}
                    totalPaid={selectedCalculation.total_paid}
                  />

                  {selectedBreakdowns.length > 0 && (
                    <View style={styles.partnerBreakdownSection}>
                      <Text style={styles.sectionTitle}>Partner Breakdown</Text>
                      {selectedBreakdowns.map((breakdown) => (
                        <PartnerBreakdownCard
                          key={breakdown.user_id}
                          email={(breakdown as any).email || 'Unknown'}
                          display_name={(breakdown as any).display_name}
                          ownershipPercentage={breakdown.ownership_percentage}
                          downPaymentShare={breakdown.down_payment_share}
                          monthlyPaymentShare={breakdown.monthly_payment_share}
                          totalInterestShare={breakdown.total_interest_share}
                          totalPaidShare={breakdown.total_paid_share}
                        />
                      ))}
                    </View>
                  )}
                </>
              )}

              <View style={styles.modalButtons}>
                <Pressable
                  onPress={() => {
                    setViewModalVisible(false);
                    setSelectedCalculation(null);
                    setSelectedBreakdowns([]);
                  }}
                  style={[styles.modalButton, styles.modalButtonPrimary]}
                >
                  <Text style={styles.modalButtonPrimaryText}>Close</Text>
                </Pressable>
              </View>
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        visible={deleteModalVisible}
        transparent={true}
        animationType="none"
        onRequestClose={cancelDeleteCalculation}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={cancelDeleteCalculation}
        >
          <Pressable style={styles.modalContent} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Delete Mortgage Calculation</Text>
            
            <Text style={styles.deleteConfirmText}>
              Are you sure you want to delete this mortgage calculation? This action cannot be undone.
            </Text>

            {calculationToDelete && (
              <View style={styles.deleteCalculationPreview}>
                <Text style={styles.deleteCalculationPreviewLabel}>Calculation Details:</Text>
                <Text style={styles.deleteCalculationPreviewText}>
                  {calculationToDelete.down_payment_type === 'percentage'
                    ? `${calculationToDelete.down_payment}%`
                    : `$${calculationToDelete.down_payment.toLocaleString()}`} down • {calculationToDelete.interest_rate * 100}% • {calculationToDelete.loan_term_years}yr
                </Text>
                <Text style={styles.deleteCalculationPreviewText}>
                  Monthly: ${calculationToDelete.monthly_payment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Text>
              </View>
            )}

            <View style={styles.modalButtons}>
              <Pressable
                onPress={cancelDeleteCalculation}
                disabled={deleting}
                style={[styles.modalButton, styles.modalButtonCancel]}
              >
                <Text style={styles.modalButtonCancelText}>Cancel</Text>
              </Pressable>
              <Pressable
                onPress={confirmDeleteCalculation}
                disabled={deleting}
                style={[
                  styles.modalButton,
                  styles.modalButtonDanger,
                  deleting && styles.modalButtonDisabled,
                ]}
              >
                <Text style={styles.modalButtonDangerText}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Comparison Modal */}
      <Modal
        visible={compareModalVisible}
        transparent={true}
        animationType="none"
        onRequestClose={() => setCompareModalVisible(false)}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => setCompareModalVisible(false)}
        >
          <Pressable style={[styles.modalContent, styles.comparisonModalContent]} onPress={(e) => e.stopPropagation()}>
            <View style={styles.comparisonHeader}>
              <Text style={styles.modalTitle}>Compare Calculations</Text>
              
              {/* Partner Selector */}
              {propertyPartners.length > 1 && (
                <View style={styles.partnerSelectorContainer}>
                  <Text style={styles.partnerSelectorLabel}>View breakdown for:</Text>
                  <View style={styles.partnerSelectorButtons}>
                    <Pressable
                      onPress={() => setSelectedPartnerId(null)}
                      style={[
                        styles.partnerSelectorButton,
                        selectedPartnerId === null && styles.partnerSelectorButtonActive,
                      ]}
                    >
                      <Text style={[
                        styles.partnerSelectorButtonText,
                        selectedPartnerId === null && styles.partnerSelectorButtonTextActive,
                      ]}>
                        Overall
                      </Text>
                    </Pressable>
                    {propertyPartners.map((partner) => (
                      <Pressable
                        key={partner.user_id}
                        onPress={() => setSelectedPartnerId(partner.user_id)}
                        style={[
                          styles.partnerSelectorButton,
                          selectedPartnerId === partner.user_id && styles.partnerSelectorButtonActive,
                        ]}
                      >
                        <Text style={[
                          styles.partnerSelectorButtonText,
                          selectedPartnerId === partner.user_id && styles.partnerSelectorButtonTextActive,
                        ]}>
                          {partner.email.split('@')[0]}
                        </Text>
                      </Pressable>
                    ))}
                  </View>
                </View>
              )}
            </View>

            <ScrollView 
              style={styles.comparisonVerticalScrollView}
              contentContainerStyle={styles.comparisonVerticalScrollContent}
              showsVerticalScrollIndicator={true}
            >
              <ScrollView 
                horizontal={true}
                showsHorizontalScrollIndicator={true}
                style={styles.comparisonScrollView}
                nestedScrollEnabled={true}
              >
                <View style={styles.comparisonTable}>
                {/* Header Row */}
                <View style={styles.comparisonRow}>
                  <View style={styles.comparisonCellHeader}>
                    <Text style={styles.comparisonCellHeaderText}>Metric</Text>
                  </View>
                  {comparisonCalculations.map((calc, idx) => (
                    <View key={calc.id} style={styles.comparisonCellHeader}>
                      <Text style={styles.comparisonCellHeaderText}>
                        Scenario {idx + 1}
                      </Text>
                      <Text style={styles.comparisonCellSubtext}>
                        {calc.down_payment_type === 'percentage'
                          ? `${calc.down_payment}%`
                          : `$${calc.down_payment.toLocaleString()}`} • {(calc.interest_rate * 100).toFixed(2)}% • {calc.loan_term_years}yr
                      </Text>
                    </View>
                  ))}
                </View>

                {/* Data Rows - Overall or Partner-specific */}
                {(() => {
                  type ComparisonRow = {
                    label: string;
                    getValue: (c: MortgageCalculation) => string;
                    highlight?: boolean;
                  };
                  
                  const rows: ComparisonRow[] = [
                    { label: 'Property Value', getValue: (c: MortgageCalculation) => `$${c.property_value.toLocaleString()}` },
                    { label: 'Down Payment (Input)', getValue: (c: MortgageCalculation) => c.down_payment_type === 'percentage' ? `${c.down_payment}%` : `$${c.down_payment.toLocaleString()}` },
                    { label: 'Down Payment ($)', getValue: (c: MortgageCalculation) => {
                      const actual = c.down_payment_type === 'percentage' 
                        ? c.property_value * (c.down_payment / 100) 
                        : c.down_payment;
                      return `$${actual.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                    }},
                    { label: 'Down Payment (%)', getValue: (c: MortgageCalculation) => {
                      const pct = c.down_payment_type === 'percentage' 
                        ? c.down_payment 
                        : (c.down_payment / c.property_value * 100);
                      return `${pct.toFixed(2)}%`;
                    }},
                    { label: 'Loan Amount', getValue: (c: MortgageCalculation) => `$${c.loan_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
                    { label: 'Interest Rate', getValue: (c: MortgageCalculation) => `${(c.interest_rate * 100).toFixed(2)}%` },
                    { label: 'Loan Term', getValue: (c: MortgageCalculation) => `${c.loan_term_years} years` },
                  ];

                  if (selectedPartnerId === null) {
                    // Overall comparison
                    rows.push(
                      { label: 'Monthly Payment', getValue: (c: MortgageCalculation) => `$${c.monthly_payment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, highlight: true },
                      { label: 'Total Interest', getValue: (c: MortgageCalculation) => `$${c.total_interest.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` },
                      { label: 'Total Paid', getValue: (c: MortgageCalculation) => `$${c.total_paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` }
                    );
                  } else {
                    // Partner-specific breakdown
                    const partner = propertyPartners.find(p => p.user_id === selectedPartnerId);
                    rows.push(
                      { label: `${partner?.email.split('@')[0]} - Down Payment Share`, getValue: (c: MortgageCalculation) => {
                        const breakdown = comparisonBreakdowns[c.id]?.find(b => b.user_id === selectedPartnerId);
                        return breakdown ? `$${breakdown.down_payment_share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A';
                      }},
                      { label: `${partner?.email.split('@')[0]} - Monthly Payment`, getValue: (c: MortgageCalculation) => {
                        const breakdown = comparisonBreakdowns[c.id]?.find(b => b.user_id === selectedPartnerId);
                        return breakdown ? `$${breakdown.monthly_payment_share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A';
                      }, highlight: true },
                      { label: `${partner?.email.split('@')[0]} - Total Interest`, getValue: (c: MortgageCalculation) => {
                        const breakdown = comparisonBreakdowns[c.id]?.find(b => b.user_id === selectedPartnerId);
                        return breakdown ? `$${breakdown.total_interest_share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A';
                      }},
                      { label: `${partner?.email.split('@')[0]} - Total Paid`, getValue: (c: MortgageCalculation) => {
                        const breakdown = comparisonBreakdowns[c.id]?.find(b => b.user_id === selectedPartnerId);
                        return breakdown ? `$${breakdown.total_paid_share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A';
                      }}
                    );
                  }

                  return rows.map((row, rowIdx) => (
                    <View key={rowIdx} style={[styles.comparisonRow, rowIdx % 2 === 0 && styles.comparisonRowEven]}>
                      <View style={styles.comparisonCellLabel}>
                        <Text style={styles.comparisonCellLabelText}>{row.label}</Text>
                      </View>
                      {comparisonCalculations.map((calc) => {
                        const value = row.getValue(calc);
                        const isBest = (row.highlight === true) && selectedPartnerId === null && row.label === 'Monthly Payment'
                          ? calc.monthly_payment === Math.min(...comparisonCalculations.map(c => c.monthly_payment))
                          : (row.highlight === true) && selectedPartnerId !== null
                          ? (() => {
                              const breakdowns = comparisonCalculations.map(c => {
                                const b = comparisonBreakdowns[c.id]?.find(bd => bd.user_id === selectedPartnerId);
                                return b?.monthly_payment_share || Infinity;
                              });
                              const currentBreakdown = comparisonBreakdowns[calc.id]?.find(b => b.user_id === selectedPartnerId);
                              return currentBreakdown?.monthly_payment_share === Math.min(...breakdowns);
                            })()
                          : false;
                        return (
                          <View key={calc.id} style={[styles.comparisonCell, isBest && styles.comparisonCellBest]}>
                            <Text style={[styles.comparisonCellValue, isBest && styles.comparisonCellValueBest]}>
                              {value}
                            </Text>
                          </View>
                        );
                      })}
                    </View>
                  ));
                })()}
                </View>
              </ScrollView>
            </ScrollView>

            <View style={styles.modalButtons}>
              <Pressable
                onPress={() => {
                  const csv = exportComparisonToCSV(comparisonCalculations, comparisonBreakdowns, propertyPartners, selectedPartnerId);
                  copyToClipboard(csv);
                  Alert.alert('Success', 'Comparison data copied to clipboard as CSV');
                }}
                style={[styles.modalButton, styles.modalButtonCancel]}
              >
                <Text style={styles.modalButtonCancelText}>Copy CSV</Text>
              </Pressable>
              <Pressable
                onPress={() => {
                  const csv = exportComparisonToCSV(comparisonCalculations, comparisonBreakdowns, propertyPartners, selectedPartnerId);
                  downloadCSV(csv, `mortgage-comparison-${new Date().toISOString().split('T')[0]}.csv`);
                  Alert.alert('Success', 'Comparison data downloaded as CSV');
                }}
                style={[styles.modalButton, styles.modalButtonCancel]}
              >
                <Text style={styles.modalButtonCancelText}>Download CSV</Text>
              </Pressable>
              <Pressable
                onPress={() => {
                  const text = exportComparisonToText(comparisonCalculations, comparisonBreakdowns, propertyPartners, selectedPartnerId);
                  copyToClipboard(text);
                  Alert.alert('Success', 'Comparison data copied to clipboard as text');
                }}
                style={[styles.modalButton, styles.modalButtonCancel]}
              >
                <Text style={styles.modalButtonCancelText}>Copy Text</Text>
              </Pressable>
              <Pressable
                onPress={() => {
                  setCompareModalVisible(false);
                  setSelectedForComparison(new Set());
                  setSelectedPartnerId(null);
                }}
                style={[styles.modalButton, styles.modalButtonPrimary]}
              >
                <Text style={styles.modalButtonPrimaryText}>Close</Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Amortization Schedule Modal */}
      <Modal
        visible={scheduleModalVisible}
        transparent={true}
        animationType="none"
        onRequestClose={() => {
          setScheduleModalVisible(false);
          setScheduleView('all');
        }}
      >
        <Pressable
          style={styles.modalOverlay}
          onPress={() => {
            setScheduleModalVisible(false);
            setScheduleView('all');
          }}
        >
          <Pressable style={[styles.modalContent, styles.scheduleModalContent]} onPress={(e) => e.stopPropagation()}>
            <Text style={styles.modalTitle}>Amortization Schedule</Text>
            {scheduleCalculation && (
              <View style={styles.scheduleHeader}>
                <Text style={styles.scheduleHeaderText}>
                  {scheduleCalculation.down_payment_type === 'percentage'
                    ? `${scheduleCalculation.down_payment}%`
                    : `$${scheduleCalculation.down_payment.toLocaleString()}`} down • {(scheduleCalculation.interest_rate * 100).toFixed(2)}% • {scheduleCalculation.loan_term_years}yr
                </Text>
              </View>
            )}

            {/* View Tabs */}
            <View style={styles.scheduleTabs}>
              <Pressable
                onPress={() => setScheduleView('all')}
                style={[styles.scheduleTab, scheduleView === 'all' && styles.scheduleTabActive]}
              >
                <Text style={[styles.scheduleTabText, scheduleView === 'all' && styles.scheduleTabTextActive]}>
                  All Payments
                </Text>
              </Pressable>
              <Pressable
                onPress={() => setScheduleView('yearly')}
                style={[styles.scheduleTab, scheduleView === 'yearly' && styles.scheduleTabActive]}
              >
                <Text style={[styles.scheduleTabText, scheduleView === 'yearly' && styles.scheduleTabTextActive]}>
                  Year Summary
                </Text>
              </Pressable>
              <Pressable
                onPress={() => setScheduleView('afterX')}
                style={[styles.scheduleTab, scheduleView === 'afterX' && styles.scheduleTabActive]}
              >
                <Text style={[styles.scheduleTabText, scheduleView === 'afterX' && styles.scheduleTabTextActive]}>
                  After X Years
                </Text>
              </Pressable>
            </View>

            <ScrollView 
              style={styles.scheduleScrollView}
              contentContainerStyle={styles.scheduleScrollContent}
            >
              {amortizationSchedule.length > 0 ? (
                <>
                  {scheduleView === 'all' && (
                    <View style={styles.scheduleTable}>
                      <View style={styles.scheduleTableHeader}>
                        <Text style={styles.scheduleTableHeaderCell}>Payment #</Text>
                        <Text style={styles.scheduleTableHeaderCell}>Date</Text>
                        <Text style={styles.scheduleTableHeaderCell}>Principal</Text>
                        <Text style={styles.scheduleTableHeaderCell}>Interest</Text>
                        <Text style={styles.scheduleTableHeaderCell}>Total</Text>
                        <Text style={styles.scheduleTableHeaderCell}>Balance</Text>
                      </View>
                      {amortizationSchedule.map((entry, idx) => (
                        <View key={entry.id || idx} style={[styles.scheduleTableRow, idx % 2 === 0 && styles.scheduleTableRowEven]}>
                          <Text style={styles.scheduleTableCell}>{entry.payment_number}</Text>
                          <Text style={styles.scheduleTableCell}>
                            {new Date(entry.payment_date).toLocaleDateString()}
                          </Text>
                          <Text style={styles.scheduleTableCell}>
                            ${entry.principal_payment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Text>
                          <Text style={styles.scheduleTableCell}>
                            ${entry.interest_payment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Text>
                          <Text style={styles.scheduleTableCell}>
                            ${entry.total_payment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Text>
                          <Text style={styles.scheduleTableCell}>
                            ${entry.remaining_balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Text>
                        </View>
                      ))}
                    </View>
                  )}

                  {scheduleView === 'yearly' && scheduleCalculation && (
                    <YearByYearSummary
                      schedule={amortizationSchedule}
                      loanTermYears={scheduleCalculation.loan_term_years}
                    />
                  )}

                  {scheduleView === 'afterX' && scheduleCalculation && (
                    <AfterXYearsView
                      schedule={amortizationSchedule}
                      loanTermYears={scheduleCalculation.loan_term_years}
                      loanAmount={scheduleCalculation.loan_amount}
                    />
                  )}
                </>
              ) : (
                <Text style={styles.emptyText}>No amortization schedule available.</Text>
              )}
            </ScrollView>

            <View style={styles.modalButtons}>
              <Pressable
                onPress={() => {
                  if (amortizationSchedule.length > 0 && scheduleCalculation) {
                    // Export to CSV
                    const csv = exportScheduleToCSV(amortizationSchedule, scheduleCalculation);
                    copyToClipboard(csv);
                    Alert.alert('Success', 'Amortization schedule copied to clipboard as CSV');
                  }
                }}
                style={[styles.modalButton, styles.modalButtonCancel]}
                disabled={amortizationSchedule.length === 0}
              >
                <Text style={styles.modalButtonCancelText}>Copy CSV</Text>
              </Pressable>
              <Pressable
                onPress={() => {
                  setScheduleModalVisible(false);
                  setScheduleView('all');
                }}
                style={[styles.modalButton, styles.modalButtonPrimary]}
              >
                <Text style={styles.modalButtonPrimaryText}>Close</Text>
              </Pressable>
            </View>
          </Pressable>
        </Pressable>
      </Modal>
    </PageContainer>
  );
}

// Helper functions for export
function exportScheduleToCSV(schedule: any[], calculation: MortgageCalculation): string {
  const headers = ['Payment #', 'Date', 'Principal', 'Interest', 'Total Payment', 'Remaining Balance'];
  const rows = schedule.map(entry => [
    entry.payment_number,
    new Date(entry.payment_date).toLocaleDateString(),
    entry.principal_payment.toFixed(2),
    entry.interest_payment.toFixed(2),
    entry.total_payment.toFixed(2),
    entry.remaining_balance.toFixed(2),
  ]);
  
  const csvContent = [
    `Mortgage Amortization Schedule - ${calculation.down_payment_type === 'percentage' ? `${calculation.down_payment}%` : `$${calculation.down_payment}`} down, ${(calculation.interest_rate * 100).toFixed(2)}%, ${calculation.loan_term_years}yr`,
    '',
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');
  
  return csvContent;
}

function copyToClipboard(text: string) {
  if (typeof navigator !== 'undefined' && navigator.clipboard) {
    navigator.clipboard.writeText(text);
  } else if (typeof document !== 'undefined') {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }
}

function downloadCSV(content: string, filename: string) {
  if (typeof document !== 'undefined') {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

// Helper to calculate actual down payment amount
function getActualDownPayment(calc: MortgageCalculation): number {
  if (calc.down_payment_type === 'percentage') {
    return calc.property_value * (calc.down_payment / 100);
  }
  return calc.down_payment;
}

// Helper to format down payment input (what user entered)
function formatDownPaymentInput(calc: MortgageCalculation): string {
  if (calc.down_payment_type === 'percentage') {
    return `${calc.down_payment}%`;
  }
  return `$${calc.down_payment.toLocaleString()}`;
}

function exportComparisonToCSV(
  calculations: MortgageCalculation[],
  breakdowns: Record<number, MortgagePartnerBreakdown[]>,
  partners: any[],
  selectedPartnerId: number | null
): string {
  const headers = ['Metric', ...calculations.map((_, idx) => `Scenario ${idx + 1}`)];
  const rows: string[][] = [];

  // Overall metrics
  rows.push(['Property Value', ...calculations.map(c => `$${c.property_value.toLocaleString()}`)]);
  // Show input format (percentage or amount as entered)
  rows.push(['Down Payment (Input)', ...calculations.map(c => formatDownPaymentInput(c))]);
  // Show calculated amount for consistent comparison
  rows.push(['Down Payment (Amount)', ...calculations.map(c => `$${getActualDownPayment(c).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)]);
  // Show percentage for comparison
  rows.push(['Down Payment (%)', ...calculations.map(c => {
    const pct = c.down_payment_type === 'percentage' ? c.down_payment : (c.down_payment / c.property_value * 100);
    return `${pct.toFixed(2)}%`;
  })]);
  rows.push(['Loan Amount', ...calculations.map(c => `$${c.loan_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)]);
  rows.push(['Interest Rate', ...calculations.map(c => `${(c.interest_rate * 100).toFixed(2)}%`)]);
  rows.push(['Loan Term', ...calculations.map(c => `${c.loan_term_years} years`)]);

  if (selectedPartnerId === null) {
    rows.push(['Monthly Payment', ...calculations.map(c => `$${c.monthly_payment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)]);
    rows.push(['Total Interest', ...calculations.map(c => `$${c.total_interest.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)]);
    rows.push(['Total Paid', ...calculations.map(c => `$${c.total_paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`)]);
  } else {
    const partner = partners.find(p => p.user_id === selectedPartnerId);
    rows.push([`${partner?.email || 'Partner'} - Down Payment Share`, ...calculations.map(c => {
      const b = breakdowns[c.id]?.find(bd => bd.user_id === selectedPartnerId);
      return b ? `$${b.down_payment_share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A';
    })]);
    rows.push([`${partner?.email || 'Partner'} - Monthly Payment`, ...calculations.map(c => {
      const b = breakdowns[c.id]?.find(bd => bd.user_id === selectedPartnerId);
      return b ? `$${b.monthly_payment_share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A';
    })]);
    rows.push([`${partner?.email || 'Partner'} - Total Interest`, ...calculations.map(c => {
      const b = breakdowns[c.id]?.find(bd => bd.user_id === selectedPartnerId);
      return b ? `$${b.total_interest_share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A';
    })]);
    rows.push([`${partner?.email || 'Partner'} - Total Paid`, ...calculations.map(c => {
      const b = breakdowns[c.id]?.find(bd => bd.user_id === selectedPartnerId);
      return b ? `$${b.total_paid_share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : 'N/A';
    })]);
  }

  return [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n');
}

function exportComparisonToText(
  calculations: MortgageCalculation[],
  breakdowns: Record<number, MortgagePartnerBreakdown[]>,
  partners: any[],
  selectedPartnerId: number | null
): string {
  const lines: string[] = [];
  lines.push('Mortgage Calculation Comparison');
  lines.push('='.repeat(50));
  lines.push('');

  calculations.forEach((calc, idx) => {
    const actualDownPayment = getActualDownPayment(calc);
    const downPaymentPct = calc.down_payment_type === 'percentage' 
      ? calc.down_payment 
      : (calc.down_payment / calc.property_value * 100);
    
    lines.push(`Scenario ${idx + 1}:`);
    lines.push(`  Property Value: $${calc.property_value.toLocaleString()}`);
    lines.push(`  Down Payment: ${formatDownPaymentInput(calc)} (= $${actualDownPayment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / ${downPaymentPct.toFixed(2)}%)`);
    lines.push(`  Interest Rate: ${(calc.interest_rate * 100).toFixed(2)}%`);
    lines.push(`  Loan Term: ${calc.loan_term_years} years`);
    lines.push(`  Loan Amount: $${calc.loan_amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
    
    if (selectedPartnerId === null) {
      lines.push(`  Monthly Payment: $${calc.monthly_payment.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      lines.push(`  Total Interest: $${calc.total_interest.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      lines.push(`  Total Paid: $${calc.total_paid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
    } else {
      const partner = partners.find(p => p.user_id === selectedPartnerId);
      const breakdown = breakdowns[calc.id]?.find(b => b.user_id === selectedPartnerId);
      if (breakdown) {
        lines.push(`  ${partner?.email || 'Partner'} Breakdown:`);
        lines.push(`    Down Payment Share: $${breakdown.down_payment_share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
        lines.push(`    Monthly Payment: $${breakdown.monthly_payment_share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
        lines.push(`    Total Interest: $${breakdown.total_interest_share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
        lines.push(`    Total Paid: $${breakdown.total_paid_share.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
      }
    }
    lines.push('');
  });

  return lines.join('\n');
}

function makeStyles(
  tokens: DesignTokens,
  utils: ReturnType<typeof createStyles>,
  isDesktop: boolean,
  isTablet: boolean,
  isMobile: boolean
) {
  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: tokens.color.background,
    },
    header: {
      marginBottom: isMobile ? utils.spacing.lg : utils.spacing.xl,
    },
    title: {
      fontSize: isMobile ? utils.typography.sizes.xl : utils.typography.sizes['2xl'],
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.heading.fontFamily,
      lineHeight: (isMobile ? utils.typography.sizes.xl : utils.typography.sizes['2xl']) * utils.typography.lineHeight.tight,
    },
    subtitle: {
      fontSize: isMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
      fontWeight: utils.typography.weights.regular,
      color: addColorOpacity(tokens.color.onSurface, 0.8),
      fontFamily: utils.typography.body.fontFamily,
      lineHeight: (isMobile ? utils.typography.sizes.sm : utils.typography.sizes.base) * utils.typography.lineHeight.normal,
    },
    createButton: {
      backgroundColor: tokens.color.primary,
      paddingVertical: utils.spacing.md,
      paddingHorizontal: utils.spacing.lg,
      borderRadius: utils.radius.md,
      alignItems: 'center',
      marginBottom: utils.spacing.lg,
    },
    createButtonText: {
      color: tokens.color.onPrimary,
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
    loadingContainer: {
      padding: utils.spacing.xl,
      alignItems: 'center',
    },
    loadingText: {
      marginTop: utils.spacing.md,
      fontSize: utils.typography.sizes.base,
      color: addColorOpacity(tokens.color.onSurface, 0.8),
      fontFamily: utils.typography.body.fontFamily,
    },
    emptyState: {
      padding: utils.spacing.xl,
      alignItems: 'center',
    },
    emptyText: {
      fontSize: utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      marginBottom: utils.spacing.sm,
      fontFamily: utils.typography.body.fontFamily,
    },
    emptySubtext: {
      fontSize: utils.typography.sizes.base,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
    },
    calculationsList: {
      gap: utils.spacing.sm,
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'flex-start',
    },
    partnerBreakdownSection: {
      marginTop: utils.spacing.lg,
      width: '100%',
    },
    sectionTitle: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onBackground,
      marginBottom: utils.spacing.md,
      fontFamily: utils.typography.heading.fontFamily,
      lineHeight: utils.typography.sizes.base * utils.typography.lineHeight.tight,
    },
    calculationParametersSection: {
      marginBottom: utils.spacing.lg,
      padding: utils.spacing.md,
      backgroundColor: tokens.color.surface,
      borderRadius: utils.radius.md,
      borderWidth: 1,
      borderColor: tokens.color.border,
    },
    parameterGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: utils.spacing.md,
    },
    parameterItem: {
      flex: 1,
      minWidth: 150,
    },
    parameterLabel: {
      fontSize: utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onSurface, 0.8),
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
    },
    parameterValue: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: utils.spacing.lg,
    },
    modalContent: {
      backgroundColor: tokens.color.background,
      borderRadius: utils.radius.lg,
      padding: utils.spacing.lg,
      width: '100%',
      maxWidth: isDesktop ? 1200 : 600,
      maxHeight: '90%',
    },
    modalDesktopLayout: {
      flexDirection: 'row',
      gap: utils.spacing.xl,
      flex: 1,
      minHeight: 400,
      ...(Platform.OS === 'web' && {
        height: '100%',
      }),
    },
    modalDesktopLeft: {
      flex: 1,
      minWidth: 0,
    },
    modalDesktopRight: {
      flex: 1,
      minWidth: 0,
      borderLeftWidth: 1,
      borderLeftColor: tokens.color.border,
      paddingLeft: utils.spacing.xl,
    },
    emptyPreview: {
      padding: utils.spacing.xl,
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 200,
    },
    emptyPreviewText: {
      fontSize: utils.typography.sizes.base,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
      fontStyle: 'italic',
    },
    modalScrollView: {
      width: '100%',
    },
    modalScrollContent: {
      paddingBottom: utils.spacing.md,
    },
    modalTitle: {
      fontSize: isMobile ? utils.typography.sizes.lg : utils.typography.sizes['2xl'],
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      marginBottom: isMobile ? utils.spacing.md : utils.spacing.lg,
      fontFamily: utils.typography.heading.fontFamily,
    },
    modalButtons: {
      flexDirection: isMobile ? 'column' : 'row',
      flexWrap: isMobile ? 'nowrap' : 'wrap',
      gap: isMobile ? utils.spacing.sm : utils.spacing.md,
      marginTop: utils.spacing.lg,
    },
    modalButton: {
      flex: isMobile ? 0 : 1,
      minWidth: isMobile ? '100%' : undefined,
      paddingVertical: isMobile ? utils.spacing.sm : utils.spacing.md,
      paddingHorizontal: isMobile ? utils.spacing.md : utils.spacing.lg,
      borderRadius: utils.radius.md,
      alignItems: 'center',
    },
    modalButtonCancel: {
      backgroundColor: tokens.color.surface,
      borderWidth: 1,
      borderColor: tokens.color.border,
    },
    modalButtonPrimary: {
      backgroundColor: tokens.color.primary,
    },
    modalButtonDisabled: {
      opacity: 0.5,
    },
    modalButtonCancelText: {
      color: tokens.color.onSurface,
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
    modalButtonPrimaryText: {
      color: tokens.color.onPrimary,
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
    modalButtonDanger: {
      backgroundColor: tokens.color.error,
    },
    modalButtonDangerText: {
      color: tokens.color.onError,
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
    deleteConfirmText: {
      fontSize: utils.typography.sizes.base,
      color: tokens.color.onSurface,
      marginBottom: utils.spacing.lg,
      fontFamily: utils.typography.body.fontFamily,
      lineHeight: utils.typography.sizes.base * 1.5,
    },
    deleteCalculationPreview: {
      backgroundColor: tokens.color.surface,
      borderRadius: utils.radius.md,
      padding: utils.spacing.md,
      marginBottom: utils.spacing.lg,
      borderWidth: 1,
      borderColor: tokens.color.border,
    },
    deleteCalculationPreviewLabel: {
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.semibold,
      color: addColorOpacity(tokens.color.onSurface, 0.8),
      marginBottom: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
    },
    deleteCalculationPreviewText: {
      fontSize: utils.typography.sizes.base,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      marginBottom: utils.spacing.xs,
    },
    calculatorSection: {
      backgroundColor: tokens.color.surface,
      borderRadius: utils.radius.lg,
      padding: utils.spacing.md,
      marginBottom: utils.spacing.md,
      borderWidth: 1,
      borderColor: tokens.color.border,
      ...(Platform.OS === 'web' && {
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.03)',
      }),
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.03,
      shadowRadius: 4,
      elevation: 1,
    },
    calculatorSectionHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: utils.spacing.md,
      paddingBottom: utils.spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
    },
    calculatorSectionTitle: {
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.heading.fontFamily,
      lineHeight: utils.typography.sizes.base * utils.typography.lineHeight.tight,
    },
    toggleCalculatorButton: {
      paddingVertical: utils.spacing.sm,
      paddingHorizontal: utils.spacing.md,
      borderRadius: utils.radius.md,
      backgroundColor: tokens.color.primaryContainer,
    },
    toggleCalculatorButtonText: {
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.primary,
      fontFamily: utils.typography.body.fontFamily,
    },
    calculatorContent: {
      width: '100%',
    },
    calculatorGrid: {
      flexDirection: isDesktop ? 'row' : 'column',
      gap: isDesktop ? utils.spacing.lg : utils.spacing.md,
      width: '100%',
    },
    calculatorInputColumn: {
      flex: 1,
      minWidth: 0,
    },
    calculatorResultsColumn: {
      flex: 1,
      minWidth: 0,
      ...(isDesktop && {
        borderLeftWidth: 1,
        borderLeftColor: tokens.color.border,
        paddingLeft: utils.spacing.lg,
      }),
      ...(isTablet && !isDesktop && {
        borderTopWidth: 1,
        borderTopColor: tokens.color.border,
        paddingTop: utils.spacing.lg,
        marginTop: utils.spacing.lg,
      }),
    },
    previewPartnerBreakdown: {
      marginTop: utils.spacing.md,
      width: '100%',
    },
    saveCalculationButton: {
      marginTop: utils.spacing.md,
      backgroundColor: tokens.color.primary,
      paddingVertical: utils.spacing.md,
      paddingHorizontal: utils.spacing.lg,
      borderRadius: utils.radius.md,
      alignItems: 'center',
      ...(Platform.OS === 'web' && {
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      }),
    },
    saveCalculationButtonDisabled: {
      opacity: 0.5,
    },
    saveCalculationButtonText: {
      color: tokens.color.onPrimary,
      fontSize: utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
    savedCalculationsSection: {
      width: '100%',
    },
    savedCalculationsHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: utils.spacing.lg,
      flexWrap: 'wrap',
      gap: utils.spacing.md,
    },
    savedCalculationsTitle: {
      fontSize: utils.typography.sizes.lg,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onBackground,
      fontFamily: utils.typography.heading.fontFamily,
      lineHeight: utils.typography.sizes.lg * utils.typography.lineHeight.tight,
    },
    headerActions: {
      flexDirection: 'row',
      gap: utils.spacing.md,
    },
    compareButton: {
      paddingVertical: utils.spacing.sm,
      paddingHorizontal: utils.spacing.md,
      borderRadius: utils.radius.md,
      backgroundColor: tokens.color.secondary,
      alignItems: 'center',
    },
    compareByPartnersButton: {
      backgroundColor: tokens.color.primary,
    },
    compareButtonText: {
      color: tokens.color.onSecondary,
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.semibold,
      fontFamily: utils.typography.body.fontFamily,
    },
    comparisonModalContent: {
      maxWidth: isDesktop ? 1400 : '98%',
      maxHeight: isMobile ? '98%' : '90%',
      width: isMobile ? '98%' : undefined,
      display: 'flex',
      flexDirection: 'column',
    },
    comparisonVerticalScrollView: {
      flex: 1,
      maxHeight: isMobile ? '100%' : 500,
    },
    comparisonVerticalScrollContent: {
      paddingBottom: utils.spacing.md,
    },
    comparisonScrollView: {
      flexGrow: 0,
    },
    comparisonTable: {
      minWidth: isMobile ? 300 : 600,
    },
    comparisonRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
    },
    comparisonRowEven: {
      backgroundColor: tokens.color.surface,
    },
    comparisonCellHeader: {
      flex: 1,
      padding: isMobile ? utils.spacing.xs : utils.spacing.md,
      paddingVertical: isMobile ? utils.spacing.sm : utils.spacing.md,
      backgroundColor: tokens.color.primaryContainer,
      borderRightWidth: 1,
      borderRightColor: tokens.color.border,
      minWidth: isMobile ? 80 : 120,
    },
    comparisonCellHeaderText: {
      fontSize: isMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onPrimaryContainer,
      fontFamily: utils.typography.body.fontFamily,
    },
    comparisonCellSubtext: {
      fontSize: isMobile ? utils.typography.sizes.xs : utils.typography.sizes.sm,
      color: addColorOpacity(tokens.color.onPrimaryContainer, 0.8),
      marginTop: utils.spacing.xs,
      fontFamily: utils.typography.body.fontFamily,
    },
    comparisonCellLabel: {
      flex: 1,
      padding: isMobile ? utils.spacing.xs : utils.spacing.md,
      paddingVertical: isMobile ? utils.spacing.sm : utils.spacing.md,
      backgroundColor: tokens.color.surface,
      borderRightWidth: 1,
      borderRightColor: tokens.color.border,
      minWidth: isMobile ? 80 : 120,
    },
    comparisonCellLabelText: {
      fontSize: isMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    comparisonCell: {
      flex: 1,
      padding: isMobile ? utils.spacing.xs : utils.spacing.md,
      paddingVertical: isMobile ? utils.spacing.sm : utils.spacing.md,
      borderRightWidth: 1,
      borderRightColor: tokens.color.border,
      minWidth: isMobile ? 80 : 120,
    },
    comparisonCellBest: {
      backgroundColor: addColorOpacity(tokens.color.success || tokens.color.primary, 0.2),
    },
    comparisonCellValue: {
      fontSize: isMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    comparisonCellValueBest: {
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.success,
    },
    scheduleModalContent: {
      maxWidth: isDesktop ? 1200 : '98%',
      maxHeight: isMobile ? '95%' : '90%',
    },
    scheduleHeader: {
      marginBottom: isMobile ? utils.spacing.sm : utils.spacing.md,
      padding: isMobile ? utils.spacing.sm : utils.spacing.md,
      backgroundColor: tokens.color.surface,
      borderRadius: utils.radius.md,
    },
    scheduleHeaderText: {
      fontSize: isMobile ? utils.typography.sizes.sm : utils.typography.sizes.base,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    scheduleTabs: {
      flexDirection: 'row',
      marginBottom: isMobile ? utils.spacing.md : utils.spacing.lg,
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
      gap: isMobile ? utils.spacing.xs : utils.spacing.sm,
    },
    scheduleTab: {
      flex: 1,
      paddingVertical: isMobile ? utils.spacing.sm : utils.spacing.md,
      paddingHorizontal: isMobile ? utils.spacing.xs : utils.spacing.md,
      borderBottomWidth: 2,
      borderBottomColor: 'transparent',
      alignItems: 'center',
    },
    scheduleTabActive: {
      borderBottomColor: tokens.color.primary,
    },
    scheduleTabText: {
      fontSize: isMobile ? utils.typography.sizes.xs : utils.typography.sizes.base,
      fontWeight: utils.typography.weights.medium,
      color: addColorOpacity(tokens.color.onSurface, 0.6),
      fontFamily: utils.typography.body.fontFamily,
    },
    scheduleTabTextActive: {
      color: tokens.color.primary,
      fontWeight: utils.typography.weights.semibold,
    },
    scheduleScrollView: {
      flex: 1,
      maxHeight: isDesktop ? 600 : 500,
    },
    scheduleScrollContent: {
      paddingBottom: utils.spacing.md,
    },
    scheduleTable: {
      borderWidth: 1,
      borderColor: tokens.color.border,
      borderRadius: utils.radius.md,
      overflow: 'hidden',
    },
    scheduleTableHeader: {
      flexDirection: 'row',
      backgroundColor: tokens.color.primaryContainer,
      borderBottomWidth: 2,
      borderBottomColor: tokens.color.border,
    },
    scheduleTableHeaderCell: {
      flex: 1,
      padding: utils.spacing.sm,
      fontSize: utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.bold,
      color: tokens.color.onPrimaryContainer,
      fontFamily: utils.typography.body.fontFamily,
      textAlign: 'center',
    },
    scheduleTableRow: {
      flexDirection: 'row',
      borderBottomWidth: 1,
      borderBottomColor: tokens.color.border,
    },
    scheduleTableRowEven: {
      backgroundColor: tokens.color.surface,
    },
    scheduleTableCell: {
      flex: 1,
      padding: utils.spacing.sm,
      fontSize: utils.typography.sizes.sm,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
      textAlign: 'center',
    },
    comparisonHeader: {
      marginBottom: isMobile ? utils.spacing.md : utils.spacing.lg,
    },
    partnerSelectorContainer: {
      marginTop: isMobile ? utils.spacing.sm : utils.spacing.md,
      padding: isMobile ? utils.spacing.sm : utils.spacing.md,
      backgroundColor: tokens.color.surface,
      borderRadius: utils.radius.md,
      borderWidth: 1,
      borderColor: tokens.color.border,
    },
    partnerSelectorLabel: {
      fontSize: isMobile ? utils.typography.sizes.xs : utils.typography.sizes.sm,
      fontWeight: utils.typography.weights.semibold,
      color: tokens.color.onSurface,
      marginBottom: isMobile ? utils.spacing.xs : utils.spacing.sm,
      fontFamily: utils.typography.body.fontFamily,
    },
    partnerSelectorButtons: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: isMobile ? utils.spacing.xs : utils.spacing.sm,
    },
    partnerSelectorButton: {
      paddingVertical: isMobile ? utils.spacing.xs : utils.spacing.sm,
      paddingHorizontal: isMobile ? utils.spacing.sm : utils.spacing.md,
      borderRadius: utils.radius.md,
      borderWidth: 1,
      borderColor: tokens.color.border,
      backgroundColor: tokens.color.background,
    },
    partnerSelectorButtonActive: {
      backgroundColor: tokens.color.primary,
      borderColor: tokens.color.primary,
    },
    partnerSelectorButtonText: {
      fontSize: isMobile ? utils.typography.sizes.xs : utils.typography.sizes.sm,
      color: tokens.color.onSurface,
      fontFamily: utils.typography.body.fontFamily,
    },
    partnerSelectorButtonTextActive: {
      color: tokens.color.onPrimary,
      fontWeight: utils.typography.weights.semibold,
    },
  });
}

